/**
 * staff-photo: the Staff editor's photo, through the photo sub-resource (CC 0465).
 *
 * A staff photo is set ONLY with setStaffPhoto (PUT /v1/staff/{id}/photo) and removed with deleteStaffPhoto. The
 * server orients, crops and re-encodes it, so the picked file goes up as it is: no resize, no re-encode here.
 * The record's create/update never carries photo or photoUrl.
 */
import { t as i18nT } from "../../lib/i18n";
import { onSessionEnd, onTenantSwitching } from "../../shell/access";

/** Once a workspace answers STAFF_PHOTO_UNAVAILABLE, upload stays off FOR THAT WORKSPACE AND SESSION: retrying won't
 *  help, and the user isn't invited to try. The first refusal is the signal; nothing is asked ahead of time.
 *  It is cleared when the workspace switches or the session ends (CC 0467), the same hooks useStaff's cache
 *  purges on, and an open editor re-reads it through subscribeStaffPhoto. */
let _unavailable = false;
const _subs = new Set<() => void>();
const _setUnavailable = (v: boolean): void => {
  if (_unavailable === v) return;
  _unavailable = v;
  for (const fn of [..._subs]) fn();
};
export const staffPhotoUnavailable = (): boolean => _unavailable;
/** Called whenever the flag changes; answers the unsubscribe. */
export const subscribeStaffPhoto = (fn: () => void): (() => void) => { _subs.add(fn); return () => { _subs.delete(fn); }; };
/* GENERATION (CC 0469). A photo request started before a workspace switch or session end can answer after it.
   Its answer belongs to the old workspace: it must not set the flag (a late 503 would switch upload off in the
   new workspace), show an error or note, or apply a URL. Every switch and session end moves the generation on. */
let _gen = 0;
const _sessionSubs = new Set<() => void>();
const _nextGeneration = (): void => { _gen += 1; _setUnavailable(false); for (const fn of [..._sessionSubs]) fn(); };
onTenantSwitching(_nextGeneration);
onSessionEnd(_nextGeneration);
/** Called when a switch or sign-out moves the generation on (CC 0470): an open editor drops its in-flight photo
 *  state (busy, error, note) there, synchronously, because the late answer itself will do nothing. */
export const subscribeStaffPhotoSession = (fn: () => void): (() => void) => { _sessionSubs.add(fn); return () => { _sessionSubs.delete(fn); }; };
/** Tests only. */
export const __resetStaffPhotoSession = (): void => { _setUnavailable(false); };

export type StaffPhotoOutcome<T> = { stale: true } | { stale: false; ok: true; value: T } | { stale: false; ok: false; text: string };
/** Run one photo request and settle it against the generation it started in. A stale answer, success or refusal,
 *  is reported as `{ stale: true }` and touches nothing: staffPhotoErrorText (and so the flag) runs only for a
 *  current refusal. */
export async function settleStaffPhoto<T>(run: () => Promise<T>, stillCurrent: () => boolean = () => true): Promise<StaffPhotoOutcome<T>> {
  const g = _gen;
  const current = () => g === _gen && stillCurrent();
  try {
    const value = await run();
    return current() ? { stale: false, ok: true, value } : { stale: true };
  } catch (e) {
    return current() ? { stale: false, ok: false, text: staffPhotoErrorText(e) } : { stale: true };
  }
}

/* THE EDITOR RULE (CC 0471). Every photo request in the Staff editor carries the (generation, member) it started
   under, and its result is applied only if the open editor is still on that generation AND that member AND still
   mounted. Otherwise it is dropped completely: no form write, no busy/error/note, no flag. The editor keeps ONE
   scope object in a ref, updated on every render, and every request reads it at settle time, never its closure. */
export interface EditorPhotoScope { memberId: string | null; mounted: boolean }
/* A FAILURE IS NEVER SILENT (CC 0474). When the editor has left the member (unmounted: Cancel, Back, the sidebar;
   or A→B on the same route) but the workspace and session are the same, a refusal is still news the user must
   get: it goes to `orphanedFailure` (the shell toast), with nothing written to any form and no flag set. A late
   success shows nothing. After a switch or sign-out, nothing at all (0469/0470). */
export async function runEditorPhoto<T>(
  scope: EditorPhotoScope, memberId: string, run: () => Promise<T>,
  on: { ok: (value: T) => void; err: (text: string) => void; orphanedFailure?: (text: string) => void },
): Promise<boolean> {
  const g = _gen;
  const here = () => scope.mounted && scope.memberId === memberId;
  try {
    const value = await run();
    if (g !== _gen || !here()) return false;
    on.ok(value);
    return true;
  } catch (e) {
    if (g !== _gen) return false;
    if (!here()) { on.orphanedFailure?.(staffPhotoMessage(e)); return false; }
    on.err(staffPhotoErrorText(e));
    return true;
  }
}

/** Upload needs the image processor; removal never does (CC 0469). */
export const photoUploadActive = (p: { persisted: boolean; canWrite: boolean; unavailable: boolean }): boolean => p.persisted && p.canWrite && !p.unavailable;
export const photoRemoveAvailable = (p: { persisted: boolean; canWrite: boolean }): boolean => p.persisted && p.canWrite;

const COPY: Record<string, [string, string]> = {
  STAFF_PHOTO_REQUIRED: ["staff.photo.required", "Choose a photo to upload."],
  STAFF_PHOTO_INVALID: ["staff.photo.invalid", "This file isn't a valid image. Choose a PNG, JPEG, WebP or HEIC photo."],
  STAFF_PHOTO_UNSUPPORTED: ["staff.photo.unsupported", "This photo couldn't be processed safely. Choose a different photo."],
  PAYLOAD_TOO_LARGE: ["staff.photo.tooLarge", "This photo is larger than 10 MB. Choose a smaller one."],
  UNSUPPORTED_MEDIA_TYPE: ["staff.photo.mediaType", "Use a PNG, JPEG, WebP or HEIC photo."],
  STAFF_PHOTO_CONFLICT: ["staff.photo.conflict", "This photo was changed at the same time. Reload and try again."],
  UPSTREAM_UNAVAILABLE: ["staff.photo.upstream", "The photo couldn't be processed right now. Try again."],
  STAFF_PHOTO_UNAVAILABLE: ["staff.photo.unavailable", "Photo upload isn't available on this workspace yet."],
};
/** Status fallbacks for an answer without one of the codes above. */
const BY_STATUS: Record<number, string> = { 413: "PAYLOAD_TOO_LARGE", 415: "UNSUPPORTED_MEDIA_TYPE", 502: "UPSTREAM_UNAVAILABLE" };

export const STAFF_PHOTO_UNAVAILABLE_COPY = (): string => i18nT(COPY.STAFF_PHOTO_UNAVAILABLE[0], COPY.STAFF_PHOTO_UNAVAILABLE[1]);
export const STAFF_PHOTO_FROM_LINK_COPY = (): string => i18nT("staff.photo.fromLink", "Upload a photo file. A photo can't be set from a link.");

/** The code a refusal carries (the wire code, or the status fallback), or "". */
export function staffPhotoCode(e: unknown): string {
  const x = e as { code?: unknown; status?: unknown } | null;
  const code = typeof x?.code === "string" ? x.code : "";
  if (code && COPY[code]) return code;
  return typeof x?.status === "number" ? BY_STATUS[x.status] ?? code : code;
}

/** Readable copy for a refused photo upload or removal. STAFF_PHOTO_UNAVAILABLE also switches upload off for the
 *  session. Anything unrecognised reads as the generic "try again". */
export function staffPhotoErrorText(e: unknown): string {
  if (staffPhotoCode(e) === "STAFF_PHOTO_UNAVAILABLE") _setUnavailable(true);
  return staffPhotoMessage(e);
}
/** The words only, with no flag side effect: for a failure reported after the editor has left the member (CC 0474). */
export function staffPhotoMessage(e: unknown): string {
  const c = COPY[staffPhotoCode(e)];
  return c ? i18nT(c[0], c[1]) : i18nT("staff.photo.failed", "The photo wasn't saved. Try again.");
}
